import { Button, Drawer, Form, Input, Select, Space } from "antd";
import { useContext, useEffect, useState } from "react";
import { IBaseDrawerModel } from "../../../Types";
import { useClient } from "../../Context/ClientContext";
import { notificationContext } from "../../Common/PageRoute";
import OrganisationsService, {
  Department_Type,
  DepartmentTypeList,
  IDepttData,
  IDepttRequestModel,
  IDesignationRequestModel,
  IOrganiseData,
  ISubUnitData,
} from "../../Services/Organisations/OrganisationsService";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { IDropdownProps } from "../Settings/SettingVoucherTemplate";

const AddDesignationModel = ({
  isModalOpen,
  setIsModalOpen,
  id,
  setTrigger,
}: IBaseDrawerModel) => {
  const [form] = Form.useForm<IDesignationRequestModel>();
  const { currentClient } = useClient();
  const [loading, setLoading] = useState<boolean>(false);
  const { openNotification } = useContext(notificationContext);
  const [orgList, setOrgList] = useState<IOrganiseData[]>([]);
  const [subUnitList, setSubUnitList] = useState<ISubUnitData[]>([]);
  const [depttList, setDepttList] = useState<IDepttData[]>([]);
  const [selOrg, setSelOrg] = useState<string>("");
  const [selSubUnit, setSelSubUnit] = useState<string>("");

  const saveData = async (formData: IDesignationRequestModel) => {
    setLoading(true);
    await OrganisationsService.postDesignation(
      currentClient?.id!,
      formData,
      id!
    )
      .then((val: IApiResponse<IIdNameModel>) => {
        openNotification(
          "success",
          `${val?.result?.name} designation ${
            id ? "updated" : "added"
          } successfully`
        );
        setIsModalOpen({
          open: false,
          id: "",
        });
        setLoading(false);
        setTrigger && setTrigger((prev) => !prev);
      })
      .catch((er) => {
        console.log("Data fetching error: ", er.message);
        setLoading(false);
      });
  };

  const submitForm = (val: IDesignationRequestModel) => {
    saveData(val);
  };

  const getData = async () => {
    setLoading(true);
    await OrganisationsService.getDesignation(currentClient?.id!, id!)
      .then((val: IDesignationRequestModel | null) => {
        setSelOrg(val?.orgId as string);
        setSelSubUnit(val?.subUnitId as string);
        let formValues = {
          designationName: val?.designationName,
          parentDesignationId: val?.parentDesignationId,
          orgId: val?.orgId,
          subUnitId: val?.subUnitId,
          depttId: val?.depttId,
        };

        form.setFieldsValue(formValues);
        setLoading(false);
      })
      .catch((er) => {
        console.log("Data fetching error: ", er.message);
        setLoading(false);
      });
  };
  useEffect(() => {
    if (id) getData();
  }, [id]);

  const getOrgList = async () => {
    setLoading(true);
    await OrganisationsService.getOrganisationsList(currentClient?.id!)
      .then((val: IOrganiseData[] | null) => {
        setOrgList(
          val?.map((org: IOrganiseData) => ({
            id: org.id,
            orgName: org.orgName,
          })) || []
        );
        setLoading(false);
      })
      .catch((er) => {
        console.log("Data fetching error: ", er.message);
        setLoading(false);
      });
  };
  const getSubUnitList = async () => {
    setLoading(true);
    await OrganisationsService.getSubUnitsList(currentClient?.id!)
      .then((val: ISubUnitData[] | null) => {
        setSubUnitList(
          val?.map((subUnit: ISubUnitData) => ({
            id: subUnit.id,
            orgName: subUnit.orgName,
            orgId: subUnit.orgId,
            subUnitName: subUnit.subUnitName,
          })) || []
        );
        setLoading(false);
      })
      .catch((er) => {
        console.log("Data fetching error: ", er.message);
        setLoading(false);
      });
  };
  const getDepttList = async () => {
    setLoading(true);
    await OrganisationsService.getDepttList(currentClient?.id!)
      .then((val: IDepttData[] | null) => {
        setDepttList(
          val?.map((deptt: IDepttData) => ({
            id: deptt.id,
            depttName: deptt.depttName,
            depttType: deptt.depttType,
            orgId: deptt.orgId,
            orgName: deptt.orgName,
            subUnitId: deptt.subUnitId,
            subUnitName: deptt.subUnitName,
          })) || []
        );
        setLoading(false);
      })
      .catch((er) => {
        console.log("Data fetching error: ", er.message);
        setLoading(false);
      });
  };
  useEffect(() => {
    getOrgList();
    getSubUnitList();
    getDepttList();
  }, [currentClient?.id]);

  return (
    <Drawer
      open={isModalOpen}
      onClose={() =>
        setIsModalOpen({
          open: false,
          id: "",
        })
      }
      title={`${id ? "Update" : "Save"} designation`}
      width={"30vw"}
      extra={
        <Space>
          <Button
            disabled={loading}
            onClick={() => {
              form.resetFields();
              setIsModalOpen({
                open: false,
                id: "",
              });
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={loading}
            loading={loading}
            type="primary"
            onClick={() => {
              form.validateFields().then(submitForm);
            }}
          >
            {`${id ? "Update" : "Save"}`}
          </Button>
        </Space>
      }
    >
      <Form name="designation" form={form} disabled={loading} layout="vertical">
        <Form.Item
          name="designationName"
          label="Designation's Name"
          rules={[
            { required: true },
            {
              pattern: /^[A-Za-z][ A-Za-z0-9_()&-.,]{2,49}$/,
              message: "Value's length must be between 3 and 50",
            },
          ]}
        >
          <Input
            type="text"
            maxLength={50}
            minLength={3}
            placeholder="Enter name here..."
          ></Input>
        </Form.Item>
        <Form.Item
          name="orgId"
          label="Organisation"
          rules={[{ required: true, message: "Please select organisation" }]}
        >
          <Select
            placeholder="Select organisation"
            onSelect={(val: string) => {
              form.setFieldValue("subUnitId", undefined);
              form.setFieldValue("depttId", undefined);
              setSelOrg(val);
              setSelSubUnit("");
            }}
            options={orgList.map((org: IOrganiseData) => ({
              label: org.orgName,
              value: org.id,
              key: org.id,
              title: "",
            }))}
          ></Select>
        </Form.Item>
        <Form.Item
          name="subUnitId"
          label="Sub Unit"
          rules={[{ required: true, message: "Please select sub unit" }]}
        >
          <Select
            disabled={selOrg === ""}
            placeholder="Select sub unit"
            onSelect={(val: string) => {
              form.setFieldValue("depttId", undefined);
              setSelSubUnit(val);
            }}
            options={subUnitList
              .filter((subUnit: ISubUnitData) => subUnit.orgId === selOrg)
              .map((subUnit: ISubUnitData) => ({
                label: subUnit.subUnitName,
                value: subUnit.id,
                key: subUnit.id,
                title: "",
              }))}
          ></Select>
        </Form.Item>
        <Form.Item
          name="depttId"
          label="Department"
          rules={[{ required: true, message: "Please select department" }]}
        >
          <Select
            disabled={selSubUnit === ""}
            placeholder="Select department"
            options={depttList
              .filter((deptt: IDepttData) => deptt.subUnitId === selSubUnit)
              .map((deptt: IDepttData) => ({
                label: deptt.depttName,
                value: deptt.id,
                key: deptt.id,
                title: "",
              }))}
          ></Select>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddDesignationModel;
